import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../services/supabaseClient';

const VisaApplicationsPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      let q = supabase
        .from('visa_applications')
        .select('*, clients!inner(first_name,last_name), visa_products!inner(country,visa_type,name)')
        .order('updated_at', { ascending: false });
      if (status !== 'all') q = q.eq('status', status);
      const { data, error } = await q;
      if (error) throw error;
      setRows(data || []);
    } catch (e) {
      setError('Failed to load visa applications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [status]);

  const filtered = (rows || []).filter(v => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const name = `${v.clients?.first_name || ''} ${v.clients?.last_name || ''}`.trim();
    const prod = `${v.visa_products?.country || ''} ${v.visa_products?.visa_type || ''} ${v.visa_products?.name || ''}`;
    const hay = `${name} ${prod} ${v.status || ''}`.toLowerCase();
    return hay.includes(q);
  });

  if (loading) return <div className="min-h-screen bg-gray-900 font-mono text-white flex items-center justify-center"><div className="animate-spin h-12 w-12 border-b-2 border-blue-500 rounded-full" /></div>;

  return (
    <div className="min-h-screen bg-gray-900 font-mono text-white p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white">
          🛂 Visa Applications
        </h2>
        <p className="text-gray-400">
          Monitor and manage visa application statuses and progress
        </p>
      </div>

      {/* Filters */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input className="px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="🔍 Search by client/product" value={search} onChange={(e) => setSearch(e.target.value)} />
          <select className="px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="all">All statuses</option>
            {['draft', 'submitted', 'biometrics', 'decision', 'approved', 'rejected'].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-800 rounded p-4 mb-6">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <p className="text-sm text-red-400">{error}</p>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden mb-6">
        <table className="min-w-full divide-y divide-gray-700">
          <thead className="bg-gray-900">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Client</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Product</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Updated</th>
              <th className="px-6 py-4" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {filtered.map(v => (
              <tr key={v.id} className="hover:bg-gray-700/50 transition-colors">
                <td className="px-6 py-4 text-sm font-semibold text-white">{`${v.clients?.first_name || ''} ${v.clients?.last_name || ''}`.trim() || '-'}</td>
                <td className="px-6 py-4 text-sm text-gray-300">{v.visa_products ? `${v.visa_products.country} • ${v.visa_products.visa_type || ''} • ${v.visa_products.name || ''}` : '-'}</td>
                <td className="px-6 py-4 text-xs">
                  <span className={`inline-flex items-center px-3 py-1.5 rounded-full font-semibold text-xs border ${v.status === 'approved' ? 'bg-green-900/20 text-green-400 border-green-800' : v.status === 'rejected' ? 'bg-red-900/20 text-red-400 border-red-800' : v.status === 'decision' ? 'bg-purple-900/20 text-purple-400 border-purple-800' : v.status === 'biometrics' ? 'bg-yellow-900/20 text-yellow-400 border-yellow-800' : 'bg-blue-900/20 text-blue-400 border-blue-800'}`}>
                    {v.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-xs text-gray-400">{v.updated_at ? new Date(v.updated_at).toLocaleString() : '-'}</td>
                <td className="px-6 py-4 text-right whitespace-nowrap space-x-2">
                  <Link to={`/admin/visa_apps/${v.id}`} className="px-3 py-1.5 rounded text-sm font-medium bg-blue-600 text-white hover:bg-blue-700">Open</Link>
                  {v.case_id && (
                    <Link to={`/admin/cases/${v.case_id}`} className="px-3 py-1.5 rounded text-sm font-medium bg-gray-700 text-white border border-gray-600 hover:bg-gray-600">Open Case</Link>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="px-6 py-12 text-center text-sm text-gray-500">No visa applications found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default VisaApplicationsPage;
